import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { useCurrency } from '../hooks/useCurrency';
import { AttendanceWorkerRow } from '../components/attendance/AttendanceWorkerRow';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Attendance = () => {
  const [workers, setWorkers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const { format: fmt } = useCurrency();

  const fetchWorkers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/workers?active_only=true`);
      setWorkers(response.data);
    } catch (error) {
      toast.error('Failed to load workers');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAttendance = useCallback(async (date) => {
    try {
      const response = await axios.get(`${API_URL}/api/attendance/${date}`);
      const attendanceMap = {};
      response.data.forEach(record => {
        attendanceMap[record.worker_id] = {
          status: record.status,
          overtime_hours: record.overtime_hours || 0
        };
      });
      setAttendance(attendanceMap);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to fetch attendance:', error?.message);
      }
    }
  }, []);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  useEffect(() => {
    if (selectedDate) {
      fetchAttendance(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate, fetchAttendance]);

  const handleMarkAttendance = async (workerId, status, overtimeHours = 0) => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    setSaving(prev => ({ ...prev, [workerId]: true }));

    try {
      await axios.post(`${API_URL}/api/attendance`, {
        worker_id: workerId,
        date: dateStr,
        status: status,
        overtime_hours: parseFloat(overtimeHours) || 0
      });

      setAttendance(prev => ({
        ...prev,
        [workerId]: { status, overtime_hours: overtimeHours }
      }));

      toast.success('Attendance marked');
    } catch (error) {
      toast.error('Failed to mark attendance');
    } finally {
      setSaving(prev => ({ ...prev, [workerId]: false }));
    }
  };

  const handleOvertimeChange = (workerId, newOvertime) => {
    setAttendance(prev => ({
      ...prev,
      [workerId]: { ...prev[workerId], overtime_hours: newOvertime }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="attendance-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E4632]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div>
        <h1
          className="text-3xl md:text-4xl font-bold text-[#1C1C1E] tracking-tight"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          Attendance
        </h1>
        <p className="text-[#5C5C5C] mt-1">
          Mark daily attendance for workers
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-white border border-[#D1D1CD] rounded-lg shadow-sm lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg font-semibold text-[#1C1C1E]">
              <CalendarDays className="w-5 h-5 text-[#2E4632]" />
              Select Date
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-md border border-[#D1D1CD]"
              data-testid="attendance-calendar"
            />
            <div className="mt-4 p-3 bg-[#F4F4F0] rounded-lg">
              <p className="text-sm font-medium text-[#5C5C5C]">Selected Date</p>
              <p className="text-lg font-semibold text-[#1C1C1E]" data-testid="selected-date">
                {format(selectedDate, 'EEEE, MMM d, yyyy')}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-[#D1D1CD] rounded-lg shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="text-lg font-semibold text-[#1C1C1E]">
                Workers ({workers.length})
              </span>
              <div className="flex gap-2 text-xs">
                <span className="flex items-center gap-1 text-[#2E4632]">
                  <div className="w-3 h-3 rounded-full bg-[#2E4632]"></div> Full
                </span>
                <span className="flex items-center gap-1 text-[#D97706]">
                  <div className="w-3 h-3 rounded-full bg-[#D97706]"></div> Half
                </span>
                <span className="flex items-center gap-1 text-[#BC4749]">
                  <div className="w-3 h-3 rounded-full bg-[#BC4749]"></div> Absent
                </span>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {workers.length === 0 ? (
              <div className="text-center py-8 text-[#5C5C5C]">
                No active workers. Add workers first.
              </div>
            ) : (
              <div className="space-y-4">
                {workers.map((worker) => {
                  const workerAttendance = attendance[worker.id] || {};
                  return (
                    <AttendanceWorkerRow
                      key={worker.id}
                      worker={worker}
                      currentStatus={workerAttendance.status || ''}
                      overtimeHours={workerAttendance.overtime_hours || 0}
                      saving={saving[worker.id]}
                      fmt={fmt}
                      onMark={handleMarkAttendance}
                      onOvertimeChange={handleOvertimeChange}
                    />
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Attendance;
